import { Injectable, NgZone } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { switchMap, tap } from 'rxjs';
import { UserMaintenanceRoutes, UserStorageRoutes } from 'src/app/core/enums/routes.enum';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { Params } from '@angular/router';
import { MaintenanceComponentTabReferrer } from 'src/app/maintenance-user/enums/maintenance-component-tab-referrer.enum';
import { MaintenanceJobTabReferrer } from 'src/app/maintenance-user/enums/maintenance-job-tab-referrer.enum';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MainTableFilterState } from 'src/app/core/state/main-table-filter.state';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { ProductStatus } from '../enums/product-status.enum';
import { IProductStock } from '../models/product-stock.model';
import {
  IApiResponseGroupProducts,
  IProductGroupData,
  IProductListItem,
  IProductResponse,
} from '../models/product.model';
import { ProductApiService } from '../services/product-api.service';
import {
  AddToStock,
  AdjustStock,
  ClearProductsByMenu,
  CreateProduct,
  DeleteProduct,
  LoadAllProducts,
  LoadProductById,
  LoadProductStock,
  LoadProducts,
  PrintProductsReport,
  UpdateProduct,
  UseProduct,
  ClearCurrentProduct,
  LoadMatchingProducts,
  TransferProduct,
  ImportStorageProducts,
} from './product.actions';
import { ProductPageReferrerEnum } from '../enums/product-page-referrer.enum';

export interface ProductStateModel {
  products: IProductResponse[];
  productsGrouped: IProductGroupData[];
  allProducts: IProductListItem[];
  allProductsPerDepartment: IdName[];
  matchingProducts: IProductResponse[];
  productsTotalPages: number;
  currentProduct: IProductResponse;
  productStock: IProductStock[];
  productsReport: Blob;
}

const PRODUCT_STATE_TOKEN = new StateToken<ProductStateModel>('products');

@State<ProductStateModel>({
  name: PRODUCT_STATE_TOKEN,
  defaults: {
    products: [],
    productsGrouped: [],
    allProducts: [],
    allProductsPerDepartment: [],
    matchingProducts: [],
    productsTotalPages: 0,
    currentProduct: <IProductResponse>{},
    productStock: [],
    productsReport: <Blob>{},
  },
})
@Injectable()
export class ProductState {
  constructor(
    private service: ProductApiService,
    private zone: NgZone,
    private store: Store
  ) {}

  @Action(LoadProducts, { cancelUncompleted: true })
  loadProducts(
    { getState, setState }: StateContext<ProductStateModel>,
    { filter, menuId }: LoadProducts
  ) {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    return this.service.getProducts(filter, menuId, allDeps).pipe(
      tap((response: IApiResponseGroupProducts) => {
        const state = getState();
        setState({
          ...state,
          products: response.data,
          productsGrouped: response.groupData,
          productsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllProducts, { cancelUncompleted: true })
  loadAllProducts(
    { getState, setState }: StateContext<ProductStateModel>,
    { excludeAttachedToMaintenanceJobId, excludeAttachedToComponentId }: LoadAllProducts
  ) {
    return this.service
      .getAllProducts(excludeAttachedToMaintenanceJobId, excludeAttachedToComponentId)
      .pipe(
        tap((response: IProductListItem[]) => {
          const state = getState();
          setState({
            ...state,
            allProducts: response,
          });
        })
      );
  }

  @Action(LoadProductById, { cancelUncompleted: true })
  loadProductById(
    { getState, setState }: StateContext<ProductStateModel>,
    { id }: LoadProductById
  ) {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    if (allDeps) {
      const productDetails = getState().products.find(
        (product: IProductResponse) => product.id === id
      );
      if (productDetails?.department?.id) {
        const productDepartmentId = productDetails.department.id;
        this.store.dispatch([
          new ClearProductsByMenu(),
          new ChangeCurrentSelectedDepartment(productDepartmentId, false, true),
        ]);
        return this.service.getProductById(id, productDepartmentId).pipe(
          tap((response: IProductResponse) => {
            const state = getState();
            setState({
              ...state,
              currentProduct: response,
            });
          })
        );
      }
    }
    return this.service.getProductById(id).pipe(
      tap((response: IProductResponse) => {
        const state = getState();
        setState({
          ...state,
          currentProduct: response,
        });
      })
    );
  }

  @Action(CreateProduct, { cancelUncompleted: true })
  @OnSuccess({ message: 'productCreatedToast' })
  createProduct(
    { setState }: StateContext<ProductStateModel>,
    { product, redirect }: CreateProduct
  ) {
    return this.service.createProduct(product).pipe(
      tap(() => {
        if (redirect) {
          const menuId =
            this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ??
            product.storageProductDetails.menuId;
          redirectAndBypassUnsavedDataCheck(`${UserStorageRoutes.List}/${menuId}`);
        }
      })
    );
  }

  @Action(UpdateProduct, { cancelUncompleted: true })
  @OnSuccess({ message: 'productUpdatedToast' })
  updateProduct(
    { setState }: StateContext<ProductStateModel>,
    { product, referrerType, referrerId, referrerParentId }: UpdateProduct
  ) {
    return this.service.updateProduct(product).pipe(
      tap(() => {
        const queryParams: Params = {};
        queryParams.referrerType = referrerType;
        queryParams.referrerId = referrerId;
        if (referrerParentId) {
          queryParams.referrerParentId = referrerParentId;
        }
        redirectAndBypassUnsavedDataCheck(
          `${UserStorageRoutes.Overview}/${product.storageProductDetails.commonDetails.id}`,
          {
            queryParams,
          }
        );
      })
    );
  }

  @Action(DeleteProduct, { cancelUncompleted: true })
  @OnSuccess({ message: 'productDeletedToast', dispatch: ResetCurrentFilter })
  deleteProduct(
    ctx: StateContext<ProductStateModel>,
    { productId, menuId, referrerType, referrerId, referrerParentId }: DeleteProduct
  ) {
    return this.service.deleteProduct(productId).pipe(
      tap(() => {
        let link = `${UserStorageRoutes.List}/${menuId}`;
        let activeTab = '';
        if (referrerType === ProductPageReferrerEnum.MaintenanceComponent) {
          link = `${UserMaintenanceRoutes.Component}/${referrerId}`;
          activeTab = MaintenanceComponentTabReferrer.ComponentSpareParts;
        } else if (
          referrerType === ProductPageReferrerEnum.MaintenanceJob &&
          referrerId &&
          referrerParentId
        ) {
          link = UserMaintenanceRoutes.JobOverview.replace(':id', referrerId).replace(
            ':componentId',
            referrerParentId
          );
          activeTab = MaintenanceJobTabReferrer.SpareParts;
        }
        redirectAndBypassUnsavedDataCheck(
          link,
          activeTab ? { queryParams: { activeTab } } : undefined
        );
      })
    );
  }

  @Action(LoadMatchingProducts, { cancelUncompleted: true })
  loadMatchingProducts(
    { getState, setState }: StateContext<ProductStateModel>,
    { sourceStorageProductId, targetDepartmentId, sourceDepartmentId }: LoadMatchingProducts
  ) {
    return this.service
      .getMatchingProducts(sourceStorageProductId, targetDepartmentId, sourceDepartmentId)
      .pipe(
        tap((response: IProductResponse[]) => {
          const state = getState();
          setState({
            ...state,
            matchingProducts: response,
          });
        })
      );
  }

  @Action(TransferProduct, { cancelUncompleted: true })
  @OnSuccess({ message: 'productTransferred' })
  transferProduct(
    ctx: StateContext<ProductStateModel>,
    { transferRequest, sourceDepartmentId, refreshList }: TransferProduct
  ) {
    return this.service.transferProduct(transferRequest, sourceDepartmentId).pipe(
      tap(() => {
        if (!refreshList) return;
        const filter = this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails);
        if (filter?.module !== MainTableFilterModulesEnum.Products) return;
        ctx.dispatch(new LoadProducts(filter, filter.additional));
      })
    );
  }

  @Action(UseProduct, { cancelUncompleted: true })
  @OnSuccess({ message: 'productUsed' })
  useProduct(ctx: StateContext<ProductStateModel>, { product }: UseProduct) {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    if (allDeps) {
      const productDetails = ctx
        .getState()
        .products.find((pr: IProductResponse) => pr.id === product.storageProductId);
      if (productDetails?.department?.id) {
        const productDepartmentId = productDetails.department.id;
        return this.service.useProduct(product, productDepartmentId).pipe(
          tap((updatedProduct) => {
            ctx.setState({
              ...ctx.getState(),
              products: ctx.getState().products.map((p) => {
                if (p.id === product.storageProductId) {
                  return this.mergeUpdatedProduct(p, updatedProduct, -product.amountUsed);
                }
                return p;
              }),
            });
          })
        );
      }
    }

    return this.service.useProduct(product).pipe(
      tap((updatedProduct) => {
        ctx.setState({
          ...ctx.getState(),
          products: ctx.getState().products.map((p) => {
            if (p.id === product.storageProductId) {
              return this.mergeUpdatedProduct(p, updatedProduct, -product.amountUsed);
            }
            return p;
          }),
          productsGrouped: ctx.getState().productsGrouped.map((group) => {
            return {
              ...group,
              itemsOnNode: group.itemsOnNode.map((p) => {
                if (p.id === product.storageProductId) {
                  return this.mergeUpdatedProduct(p, updatedProduct, -product.amountUsed);
                }
                return p;
              }),
            };
          }),
        });
        this.zone.run(() => {
          ctx.dispatch(new LoadProductById(product.storageProductId));
          ctx.dispatch(new LoadProductStock(product.storageProductId));
        });
      })
    );
  }

  @Action(AddToStock, { cancelUncompleted: true })
  @OnSuccess({ message: 'productAddedToStock' })
  addToStock(ctx: StateContext<ProductStateModel>, { product, redirect }: AddToStock) {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    if (allDeps) {
      const productDetails = ctx
        .getState()
        .products.find((pr: IProductResponse) => pr.id === product.storageProductId);
      if (productDetails?.department?.id) {
        const productDepartmentId = productDetails.department.id;
        return this.service.addtoStock(product, productDepartmentId).pipe(
          tap((updatedProduct) => {
            ctx.setState({
              ...ctx.getState(),
              products: ctx.getState().products.map((p) => {
                if (p.id === product.storageProductId) {
                  return this.mergeUpdatedProduct(p, updatedProduct, product.amountUsed);
                }
                return p;
              }),
            });
          })
        );
      }
    }
    return this.service.addtoStock(product).pipe(
      tap((updatedProduct) => {
        ctx.setState({
          ...ctx.getState(),
          products: ctx.getState().products.map((p) => {
            if (p.id === product.storageProductId) {
              return this.mergeUpdatedProduct(p, updatedProduct, product.amountUsed);
            }
            return p;
          }),
          productsGrouped: ctx.getState().productsGrouped.map((group) => {
            return {
              ...group,
              itemsOnNode: group.itemsOnNode.map((p) => {
                if (p.id === product.storageProductId) {
                  return this.mergeUpdatedProduct(p, updatedProduct, product.amountUsed);
                }
                return p;
              }),
            };
          }),
        });
        if (redirect) {
          redirectAndBypassUnsavedDataCheck(
            `${UserStorageRoutes.Overview}/${product.storageProductId}`
          );
        }
      })
    );
  }

  @Action(AdjustStock, { cancelUncompleted: true })
  @OnSuccess({ message: 'productStockAdjusted' })
  adjustStock(ctx: StateContext<ProductStateModel>, { product }: AdjustStock) {
    return this.service
      .adjustStock(product)
      .pipe(
        switchMap(() =>
          ctx.dispatch([
            new LoadProductById(product.storageProductId),
            new LoadProductStock(product.storageProductId),
          ])
        )
      );
  }

  @Action(LoadProductStock, { cancelUncompleted: true })
  getProductStock(
    { getState, setState }: StateContext<ProductStateModel>,
    { productId }: LoadProductStock
  ) {
    setState({
      ...getState(),
      productStock: [],
    });

    return this.service.getProductStock(productId).pipe(
      tap((response: IProductStock[]) => {
        const state = getState();
        setState({
          ...state,
          productStock: response,
        });
      })
    );
  }

  @Action(PrintProductsReport, { cancelUncompleted: true })
  printProductsReport(
    { getState, setState }: StateContext<ProductStateModel>,
    { filter, menuId }: PrintProductsReport
  ) {
    return this.service.printReport(filter, menuId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          productsReport: file,
        });
      })
    );
  }

  @Action(ImportStorageProducts, { cancelUncompleted: true })
  @OnSuccess({ message: 'storageProductsImported' })
  importStorageProducts(
    ctx: StateContext<ProductStateModel>,
    { productImport }: ImportStorageProducts
  ) {
    return this.service.importStorageProducts(productImport).pipe(
      tap(() => {
        const currentFilter = this.store.selectSnapshot(
          MainTableFilterState.getCurrentFilterDetails
        );
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);

        ctx.dispatch(new LoadProducts(currentFilter, menuId));
      })
    );
  }

  @Action(ClearProductsByMenu, { cancelUncompleted: true })
  clearProductsByMenu(ctx: StateContext<ProductStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      products: [],
      productsGrouped: [],
      productsTotalPages: 0,
    });
  }

  @Action(ClearCurrentProduct)
  clearCurrentProduct(ctx: StateContext<ProductStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentProduct: <IProductResponse>{},
    });
  }

  @Selector()
  static getProductsTotalPages(state: ProductStateModel) {
    return state.productsTotalPages;
  }

  @Selector()
  static getProductById(state: ProductStateModel): IProductResponse {
    return state.currentProduct;
  }

  @Selector()
  static getProducts(state: ProductStateModel): IProductResponse[] {
    return state.products;
  }

  @Selector()
  static getProductsGrouped(state: ProductStateModel): IProductGroupData[] {
    return state.productsGrouped;
  }

  @Selector()
  static getAllProducts(state: ProductStateModel): IProductListItem[] {
    return state.allProducts;
  }

  @Selector()
  static getProductStock(state: ProductStateModel): IProductStock[] {
    return state.productStock;
  }

  @Selector()
  static getMatchingProducts(state: ProductStateModel): IProductResponse[] {
    return state.matchingProducts;
  }

  @Selector()
  static getProductStockById(state: ProductStateModel) {
    return (id: string) => {
      return state.productStock?.find((productStock: IProductStock) => productStock.id === id);
    };
  }

  @Selector()
  static getProductsReport(state: ProductStateModel): Blob {
    return state.productsReport;
  }

  private mergeUpdatedProduct(
    product: IProductResponse,
    updatedProduct: Partial<IProductResponse> | null,
    quantityChange: number
  ): IProductResponse {
    const updatedProductFields = this.getDefinedProductFields(updatedProduct);
    const quantity = +product.quantity + +quantityChange;
    return {
      ...product,
      ...updatedProductFields,
      quantity,
      status: this.getStatusForQuantity(product, quantity),
      department: updatedProductFields.department ?? product.department,
    };
  }

  private getDefinedProductFields(
    product: Partial<IProductResponse> | null
  ): Partial<IProductResponse> {
    if (!product) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(product).filter(([, value]) => value !== undefined)
    ) as Partial<IProductResponse>;
  }

  private getStatusForQuantity(
    product: IProductResponse,
    quantity: number
  ): IProductResponse['status'] {
    if (quantity < product.minimumQuantity) {
      return ProductStatus.Red as unknown as IProductResponse['status'];
    }

    if (quantity < product.thresholdWarning) {
      return ProductStatus.Yellow as unknown as IProductResponse['status'];
    }

    return ProductStatus.Green as unknown as IProductResponse['status'];
  }
}
